import { readSseStream } from "@thekrystalship/krystal/lib/sse";

// authorizedFetch.js — the result shape every call to the anchor and the components answers in.
//
// The session (`@thekrystalship/auth-client`, held in `sessionStore.js`) attaches the bearer, renews
// ahead of expiry, and replays a refused call once. This module is the thin adapter over it that gives
// each call site one shape and no exceptions:
//
//   { ok, status, body }    what the server answered; `body` is the parsed JSON or null
//   status 0, `unreachable` a transport failure, told apart from any answer a server gave
//   status 401, `unauthenticated`  no session could be made live, and no request was made
//
// ── Three modes, and the choice is about the REQUEST ────────────────────────
//   json    a call that may be sent twice. A 401 renews once and replays it.
//   once    a call that may not. The token is renewed ahead of it and the refusal is reported as it
//           stands.
//   stream  a long read. The dial renews once on a 401; a stream that ENDS is the caller's to reopen,
//           because the reasons a read stops are the caller's business.
//
// `json` is the ordinary answer, because a 401 means the server did nothing: the request was refused
// before it was handled, so sending it again is the first time it happens. `once` is for a call whose
// cost is in the SENDING rather than in the handling.
//
// `session` is anything with the package's `fresh()` and `authorizedFetch(input, init)`.

const netResult = () => ({ ok: false, status: 0, body: null, unreachable: true });
const noSession = () => ({ ok: false, status: 401, body: null, unauthenticated: true });
const isSessionOver = (e) => !!e && (e.code === "ended" || e.code === "none");

// The parsed body, or null. A body that is not JSON is not an error here — an empty 204 and an
// error page are both "nothing to read", and the status already says which.
async function bodyOf(res) {
  if (res.status === 204) return null;
  try { return await res.json(); } catch { return null; }
}

const result = async (res) => ({ ok: res.ok, status: res.status, body: await bodyOf(res) });

/** The authorized surface for one session. Bind it where the call is made. */
export function authorized(session) {
  if (!session || typeof session.fresh !== "function" || typeof session.authorizedFetch !== "function")
    throw new TypeError("authorized() takes a session: { fresh, authorizedFetch }");

  // One request through the session; the answer, or the result that says why there is none.
  async function send(url, init) {
    try {
      return { res: await session.authorizedFetch(url, init) };
    } catch (e) {
      if (e && e.name === "AbortError") throw e;
      return { fail: isSessionOver(e) ? noSession() : netResult() };
    }
  }

  async function json(url, init) {
    const sent = await send(url, init);
    return sent.fail || result(sent.res);
  }

  async function once(url, init) {
    let token;
    try { token = await session.fresh(); } catch (e) { return isSessionOver(e) ? noSession() : netResult(); }
    let res;
    try {
      res = await fetch(url, { ...init, headers: { ...((init && init.headers) || {}), Authorization: "Bearer " + token } });
    } catch (e) {
      if (e && e.name === "AbortError") throw e;
      return netResult();
    }
    return result(res);
  }

  /**
   * A long read over SSE. `onEvent` takes each parsed frame; `stop()` ends it.
   *
   * `fetch` rather than `EventSource` for the ordinary reason: `EventSource` sends no `Authorization`
   * header, and a token does not belong in a query string.
   */
  function stream(url, { accept = "text/event-stream", onEvent, signal } = {}) {
    const control = new AbortController();
    if (signal) signal.addEventListener("abort", () => control.abort(), { once: true });

    const run = async () => {
      let sent;
      try {
        sent = await send(url, { headers: { Accept: accept }, signal: control.signal });
      } catch (e) {
        if (e && e.name === "AbortError") return { ok: true, status: 0, body: null };
        return netResult();
      }
      if (sent.fail) return sent.fail;
      const res = sent.res;
      if (!res.ok) return { ok: false, status: res.status, body: null };

      try {
        await readSseStream(res, (evt) => { if (evt) onEvent && onEvent(evt); }, control.signal);
      } catch (e) {
        if (!(e && e.name === "AbortError")) return netResult();
      }
      return { ok: true, status: res.status, body: null };
    };

    return { stopped: run(), stop: () => control.abort() };
  }

  return { json, once, stream };
}
